import {
	BiometricAuthError,
	BiometryType,
	NativeBiometric
} from '@capgo/capacitor-native-biometric';
import { esApp } from './plataforma';

/**
 * Face ID, Touch ID o huella dentro de la app: la única puerta al plugin.
 *
 * El resto de la aplicación no importa `@capgo/capacitor-native-biometric`: usa estas funciones.
 * Así, cambiar de plugin es tocar este fichero, y en los tests basta con simular estas funciones.
 *
 * Lo que ha decidido el usuario (activarla o no) está en `preferenciaBiometria.ts`, que no carga
 * el plugin.
 *
 * La biometría no dice QUIÉN es el usuario, solo que el dueño del móvil ha confirmado. Sirve para
 * desbloquear una sesión que ya existe; no sustituye al login.
 *
 * En la web no hace nada: no hay biometría y nunca se llama al plugin.
 */

/** Cómo se llama lo que tiene el móvil, para los textos. */
export type TipoDeBiometria = 'face-id' | 'touch-id' | 'huella' | 'biometria';

export type ResultadoDeVerificacion =
	| 'ok'
	| 'cancelado'
	| 'bloqueado'
	| 'no-disponible'
	| 'fallido';

export interface TextosDeVerificacion {
	/** Por qué se pide (iOS lo enseña debajo de Face ID; Android, en el diálogo). */
	motivo: string;
	titulo: string;
	cancelar: string;
}

const NOMBRES: Partial<Record<BiometryType, TipoDeBiometria>> = {
	[BiometryType.FACE_ID]: 'face-id',
	[BiometryType.TOUCH_ID]: 'touch-id',
	[BiometryType.FINGERPRINT]: 'huella'
};

/**
 * Qué hacer con cada error del plugin. Lo que no está aquí (un fallo del sensor, que puede ser
 * pasajero, o uno desconocido) es «fallido»: se puede reintentar y no desactiva nada.
 */
const RESULTADOS_DE_ERROR: Partial<Record<BiometricAuthError, ResultadoDeVerificacion>> = {
	[BiometricAuthError.USER_CANCEL]: 'cancelado',
	[BiometricAuthError.SYSTEM_CANCEL]: 'cancelado',
	[BiometricAuthError.APP_CANCEL]: 'cancelado',
	[BiometricAuthError.USER_FALLBACK]: 'cancelado',
	[BiometricAuthError.USER_LOCKOUT]: 'bloqueado',
	[BiometricAuthError.USER_TEMPORARY_LOCKOUT]: 'bloqueado',
	// Solo cuando el móvil ya no tiene con qué: la ha quitado, o ya no tiene código.
	[BiometricAuthError.BIOMETRICS_NOT_ENROLLED]: 'no-disponible',
	[BiometricAuthError.PASSCODE_NOT_SET]: 'no-disponible'
};

/**
 * Traduce un error del plugin a lo que la pantalla tiene que hacer. Es la única traducción: la
 * usan `verificarIdentidad` y `accesoGuardado`, así que un código nuevo se añade en un sitio.
 * Capacitor rechaza con el código como texto (`code: '16'`).
 */
export const resultadoDeError = (error: unknown): ResultadoDeVerificacion => {
	const codigo = Number((error as { code?: string } | undefined)?.code) as BiometricAuthError;

	return RESULTADOS_DE_ERROR[codigo] ?? 'fallido';
};

/**
 * Lo que tiene configurado el móvil, o `null` si no hay nada que ofrecer.
 *
 * El código del móvil solo no cuenta (`useFallback: false`): se ofrece «entrar con Face ID», no
 * «entrar con tu PIN».
 */
export const tipoDeBiometria = async (): Promise<TipoDeBiometria | null> => {
	if (!esApp()) {
		return null;
	}

	try {
		const { isAvailable, biometryType } = await NativeBiometric.isAvailable({ useFallback: false });

		return isAvailable ? NOMBRES[biometryType] ?? 'biometria' : null;
	} catch {
		return null;
	}
};

/**
 * Pide la cara o el dedo con el diálogo del sistema.
 *
 * Si falla, se puede usar el código del móvil (iOS); en Android hay varios intentos. Nunca lanza:
 * cada error del sistema se traduce a lo que la pantalla tiene que hacer.
 */
export const verificarIdentidad = async ({
	motivo,
	titulo,
	cancelar
}: TextosDeVerificacion): Promise<ResultadoDeVerificacion> => {
	if (!esApp()) {
		return 'no-disponible';
	}

	try {
		await NativeBiometric.verifyIdentity({
			reason: motivo,
			title: titulo,
			negativeButtonText: cancelar,
			useFallback: true,
			maxAttempts: 3
		});

		return 'ok';
	} catch (error) {
		return resultadoDeError(error);
	}
};
